import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import Link from '@mui/material/Link';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import SouthOutlinedIcon from '@mui/icons-material/SouthOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const ALine = (LL) => {
  if (LL <= 25.5) return 4;
  return 0.73 * (LL - 20);
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  const num = Number(value);
  return Number.isFinite(num) ? num : NaN;
};

const makeLeaf = (id, label) => ({ id, label, children: [] });
const makeNode = (id, label, children = []) => ({ id, label, children });

const makeFineRetainedTree = (prefix, names) =>
  makeNode(`${prefix}_retained`, 'No. 200 retained < 30%?', [
    makeNode(`${prefix}_ret_lt30`, '< 30%: No. 200 retained < 15%?', [
      makeLeaf(`${prefix}_ret_lt15`, `Group name: ${names[0]}`),
      makeNode(`${prefix}_ret_15_30`, '15-30%: Sand >= Gravel?', [
        makeLeaf(`${prefix}_ret_15_30_sand_ge`, `Group name: ${names[1]}`),
        makeLeaf(`${prefix}_ret_15_30_sand_lt`, `Group name: ${names[2]}`),
      ]),
    ]),
    makeNode(`${prefix}_ret_ge30`, '>= 30%: Sand >= Gravel?', [
      makeNode(`${prefix}_ret_ge30_sand_ge`, 'Sand >= Gravel: Gravel < 15%?', [
        makeLeaf(`${prefix}_ret_ge30_sand_ge_gravel_lt`, `Group name: ${names[3]}`),
        makeLeaf(`${prefix}_ret_ge30_sand_ge_gravel_ge`, `Group name: ${names[4]}`),
      ]),
      makeNode(`${prefix}_ret_ge30_sand_lt`, 'Sand < Gravel: Sand < 15%?', [
        makeLeaf(`${prefix}_ret_ge30_sand_lt_sand_lt`, `Group name: ${names[5]}`),
        makeLeaf(`${prefix}_ret_ge30_sand_lt_sand_ge`, `Group name: ${names[6]}`),
      ]),
    ]),
  ]);

const makeGroupSymbolNode = (prefix, symbol, names) =>
  makeNode(`${prefix}_symbol`, `Group symbol: ${symbol}`, [makeFineRetainedTree(prefix, names)]);

const FINE_INORG_LT50 = [
  {
    id: 'fine_inorg_lt50_cl',
    edge: 'PI > 7 and PI >= A-line',
    symbol: 'CL',
    names: [
      'Lean Clay',
      'Lean clay with sand',
      'Lean clay with gravel',
      'Sandy lean clay',
      'Sandy lean clay with gravel',
      'Gravelly lean clay',
      'Gravelly lean clay with sand',
    ],
  },
  {
    id: 'fine_inorg_lt50_clml',
    edge: '4 <= PI <= 7 and PI >= A-line',
    symbol: 'CL-ML',
    names: [
      'Silty Clay',
      'Silty clay with sand',
      'Silty clay with gravel',
      'Sandy silty clay',
      'Sandy silty clay with gravel',
      'Gravelly silty clay',
      'Gravelly silty clay with sand',
    ],
  },
  {
    id: 'fine_inorg_lt50_ml',
    edge: 'PI < 4 or PI < A-line',
    symbol: 'ML',
    names: [
      'Silt',
      'Silt with sand',
      'Silt with gravel',
      'Sandy silt',
      'Sandy silt with gravel',
      'Gravelly silt',
      'Gravelly silt with sand',
    ],
  },
];

const FINE_ORG_LT50 = [
  {
    id: 'fine_org_lt50_ol_clay',
    edge: 'PI >= 4 and PI >= A-line',
    symbol: 'OL',
    names: [
      'Organic clay',
      'Organic clay with sand',
      'Organic clay with gravel',
      'Sandy organic clay',
      'Sandy organic clay with gravel',
      'Gravelly organic clay',
      'Gravelly organic clay with sand',
    ],
  },
  {
    id: 'fine_org_lt50_ol_silt',
    edge: 'PI < 4 or PI < A-line',
    symbol: 'OL',
    names: [
      'Organic silt',
      'Organic silt with sand',
      'Organic silt with gravel',
      'Sandy organic silt',
      'Sandy organic silt with gravel',
      'Gravelly organic silt',
      'Gravelly organic silt with sand',
    ],
  },
];

const FINE_INORG_GTE50 = [
  {
    id: 'fine_inorg_gte50_ch',
    edge: 'PI >= A-line',
    symbol: 'CH',
    names: [
      'Fat Clay',
      'Fat clay with sand',
      'Fat clay with gravel',
      'Sandy fat clay',
      'Sandy fat clay with gravel',
      'Gravelly fat clay',
      'Gravelly fat clay with sand',
    ],
  },
  {
    id: 'fine_inorg_gte50_mh',
    edge: 'PI < A-line',
    symbol: 'MH',
    names: [
      'Elastic silt',
      'Elastic silt with sand',
      'Elastic silt with gravel',
      'Sandy elastic silt',
      'Sandy elastic silt with gravel',
      'Gravelly elastic silt',
      'Gravelly elastic silt with sand',
    ],
  },
];

const FINE_ORG_GTE50 = [
  {
    id: 'fine_org_gte50_oh_clay',
    edge: 'PI >= A-line',
    symbol: 'OH',
    names: [
      'Organic clay',
      'Organic clay with sand',
      'Organic clay with gravel',
      'Sandy organic clay',
      'Sandy organic clay with gravel',
      'Gravelly organic clay',
      'Gravelly organic clay with sand',
    ],
  },
  {
    id: 'fine_org_gte50_oh_silt',
    edge: 'PI < A-line',
    symbol: 'OH',
    names: [
      'Organic silt',
      'Organic silt with sand',
      'Organic silt with gravel',
      'Sandy organic silt',
      'Sandy organic silt with gravel',
      'Gravelly organic silt',
      'Gravelly organic silt with sand',
    ],
  },
];

const makeFinePiNode = (id, label, options) =>
  makeNode(
    id,
    label,
    options.map((option) => ({
      ...makeGroupSymbolNode(option.id, option.symbol, option.names),
      edge: option.edge,
    })),
  );

const buildFineBranch = () =>
  makeNode('fine_branch', 'Fine-grained (P200 >= 50%)', [
    makeNode('fine_ll_lt50', 'LL < 50%', [
      makeNode('fine_ll_lt50_inorg', 'Inorganic', [makeFinePiNode('fine_ll_lt50_inorg_pi', 'PI vs A-line', FINE_INORG_LT50)]),
      makeNode('fine_ll_lt50_org', 'Organic', [makeFinePiNode('fine_ll_lt50_org_pi', 'PI vs A-line', FINE_ORG_LT50)]),
    ]),
    makeNode('fine_ll_gte50', 'LL >= 50%', [
      makeNode('fine_ll_gte50_inorg', 'Inorganic', [makeFinePiNode('fine_ll_gte50_inorg_pi', 'PI vs A-line', FINE_INORG_GTE50)]),
      makeNode('fine_ll_gte50_org', 'Organic', [makeFinePiNode('fine_ll_gte50_org_pi', 'PI vs A-line', FINE_ORG_GTE50)]),
    ]),
  ]);

const makeCoarseGroupNode = (prefix, symbol, lowName, highName, label, lowEdge, highEdge) =>
  makeNode(`${prefix}_symbol`, `Group symbol: ${symbol}`, [
    makeNode(`${prefix}_content`, label, [
      { ...makeLeaf(`${prefix}_content_low`, `Group name: ${lowName}`), edge: lowEdge },
      { ...makeLeaf(`${prefix}_content_high`, `Group name: ${highName}`), edge: highEdge },
    ]),
  ]);

const buildCoarseBranch = () =>
  makeNode('coarse_branch', 'Coarse-grained (P200 < 50%)', [
    makeNode('coarse_gravel_dominant', 'Gravel >= Sand', [
      makeNode('coarse_gravel_fines_lt5', 'Fines < 5%', [
        makeNode('coarse_gravel_fines_lt5_cucc_good', 'Cu >= 4 and 1 <= Cc <= 3', [
          makeCoarseGroupNode(
            'coarse_gravel_fines_lt5_cucc_good',
            'GW',
            'Well-graded gravel',
            'Well-graded gravel with sand',
            'Sand < 15%?',
            '< 15%',
            '>= 15%',
          ),
        ]),
        makeNode('coarse_gravel_fines_lt5_cucc_poor', 'Cu < 4 or Cc < 1 or Cc > 3', [
          makeCoarseGroupNode(
            'coarse_gravel_fines_lt5_cucc_poor',
            'GP',
            'Poorly graded gravel',
            'Poorly graded gravel with sand',
            'Sand < 15%?',
            '< 15%',
            '>= 15%',
          ),
        ]),
      ]),
      makeNode('coarse_gravel_fines_5_12', '5% <= Fines <= 12%', [
        makeNode('coarse_gravel_fines_5_12_cucc_good', 'Cu >= 4 and 1 <= Cc <= 3', [
          makeNode('coarse_gravel_fines_5_12_cucc_good_pi_silt', 'PI < 4 or PI < A-line', [
            makeCoarseGroupNode(
              'coarse_gravel_fines_5_12_cucc_good_pi_silt',
              'GW-GM',
              'Well-graded gravel with silt',
              'Well-graded gravel with silt and sand',
              'Sand < 15%?',
              '< 15%',
              '>= 15%',
            ),
          ]),
          makeNode('coarse_gravel_fines_5_12_cucc_good_pi_clay', 'PI >= 7 or 4 <= PI <= 7 or PI == A-line', [
            makeCoarseGroupNode(
              'coarse_gravel_fines_5_12_cucc_good_pi_clay',
              'GW-GC',
              'Well-graded gravel with clay (or silty clay)',
              'Well-graded gravel with clay and sand (or silty clay and sand)',
              'Sand < 15%?',
              '< 15%',
              '>= 15%',
            ),
          ]),
        ]),
        makeNode('coarse_gravel_fines_5_12_cucc_poor', 'Cu < 4 or Cc < 1 or Cc > 3', [
          makeNode('coarse_gravel_fines_5_12_cucc_poor_pi_silt', 'PI < 4 or PI < A-line', [
            makeCoarseGroupNode(
              'coarse_gravel_fines_5_12_cucc_poor_pi_silt',
              'GP-GM',
              'Poorly graded gravel with silt',
              'Poorly graded gravel with silt and sand',
              'Sand < 15%?',
              '< 15%',
              '>= 15%',
            ),
          ]),
          makeNode('coarse_gravel_fines_5_12_cucc_poor_pi_clay', 'PI >= 7 or 4 <= PI <= 7 or PI == A-line', [
            makeCoarseGroupNode(
              'coarse_gravel_fines_5_12_cucc_poor_pi_clay',
              'GP-GC',
              'Poorly graded gravel with clay (or silty clay)',
              'Poorly graded gravel with clay and sand (or silty clay and sand)',
              'Sand < 15%?',
              '< 15%',
              '>= 15%',
            ),
          ]),
        ]),
      ]),
      makeNode('coarse_gravel_fines_gt12', 'Fines > 12%', [
        makeNode('coarse_gravel_fines_gt12_pi_silt', 'PI < 4 or PI < A-line', [
          makeCoarseGroupNode(
            'coarse_gravel_fines_gt12_pi_silt',
            'GM',
            'Silty gravel',
            'Silty gravel with sand',
            'Sand < 15%?',
            '< 15%',
            '>= 15%',
          ),
        ]),
        makeNode('coarse_gravel_fines_gt12_pi_clay', 'PI >= 7 and PI >= A-line', [
          makeCoarseGroupNode(
            'coarse_gravel_fines_gt12_pi_clay',
            'GC',
            'Clayey gravel (or silty clay)',
            'Clayey gravel with sand',
            'Sand < 15%?',
            '< 15%',
            '>= 15%',
          ),
        ]),
        makeNode('coarse_gravel_fines_gt12_pi_mixed', '4 <= PI <= 7 or PI == A-line', [
          makeCoarseGroupNode(
            'coarse_gravel_fines_gt12_pi_mixed',
            'GC-GM',
            'Silty, clayey gravel',
            'Silty, clayey gravel with sand',
            'Sand < 15%?',
            '< 15%',
            '>= 15%',
          ),
        ]),
      ]),
    ]),
    makeNode('coarse_sand_dominant', 'Sand > Gravel', [
      makeNode('coarse_sand_fines_lt5', 'Fines < 5%', [
        makeNode('coarse_sand_fines_lt5_cucc_good', 'Cu >= 6 and 1 <= Cc <= 3', [
          makeCoarseGroupNode(
            'coarse_sand_fines_lt5_cucc_good',
            'SW',
            'Well-graded sand',
            'Well-graded sand with gravel',
            'Gravel < 15%?',
            '< 15%',
            '>= 15%',
          ),
        ]),
        makeNode('coarse_sand_fines_lt5_cucc_poor', 'Cu < 6 or Cc < 1 or Cc > 3', [
          makeCoarseGroupNode(
            'coarse_sand_fines_lt5_cucc_poor',
            'SP',
            'Poorly graded sand',
            'Poorly graded sand with gravel',
            'Gravel < 15%?',
            '< 15%',
            '>= 15%',
          ),
        ]),
      ]),
      makeNode('coarse_sand_fines_5_12', '5% <= Fines <= 12%', [
        makeNode('coarse_sand_fines_5_12_cucc_good', 'Cu >= 6 and 1 <= Cc <= 3', [
          makeNode('coarse_sand_fines_5_12_cucc_good_pi_silt', 'PI < 4 or PI < A-line', [
            makeCoarseGroupNode(
              'coarse_sand_fines_5_12_cucc_good_pi_silt',
              'SW-SM',
              'Well-graded sand with silt',
              'Well-graded sand with silt and gravel',
              'Gravel < 15%?',
              '< 15%',
              '>= 15%',
            ),
          ]),
          makeNode('coarse_sand_fines_5_12_cucc_good_pi_clay', 'PI >= 7 or 4 <= PI <= 7 or PI == A-line', [
            makeCoarseGroupNode(
              'coarse_sand_fines_5_12_cucc_good_pi_clay',
              'SW-SC',
              'Well-graded sand with clay (or silty clay)',
              'Well-graded sand with clay and gravel (or silty clay and gravel)',
              'Gravel < 15%?',
              '< 15%',
              '>= 15%',
            ),
          ]),
        ]),
        makeNode('coarse_sand_fines_5_12_cucc_poor', 'Cu < 6 or Cc < 1 or Cc > 3', [
          makeNode('coarse_sand_fines_5_12_cucc_poor_pi_silt', 'PI < 4 or PI < A-line', [
            makeCoarseGroupNode(
              'coarse_sand_fines_5_12_cucc_poor_pi_silt',
              'SP-SM',
              'Poorly graded sand with silt',
              'Poorly graded sand with silt and gravel',
              'Gravel < 15%?',
              '< 15%',
              '>= 15%',
            ),
          ]),
          makeNode('coarse_sand_fines_5_12_cucc_poor_pi_clay', 'PI >= 7 or 4 <= PI <= 7 or PI == A-line', [
            makeCoarseGroupNode(
              'coarse_sand_fines_5_12_cucc_poor_pi_clay',
              'SP-SC',
              'Poorly graded sand with clay (or silty clay)',
              'Poorly graded sand with clay and gravel (or silty clay and gravel)',
              'Gravel < 15%?',
              '< 15%',
              '>= 15%',
            ),
          ]),
        ]),
      ]),
      makeNode('coarse_sand_fines_gt12', 'Fines > 12%', [
        makeNode('coarse_sand_fines_gt12_pi_silt', 'PI < 4 or PI < A-line', [
          makeCoarseGroupNode(
            'coarse_sand_fines_gt12_pi_silt',
            'SM',
            'Silty sand',
            'Silty sand with gravel',
            'Gravel < 15%?',
            '< 15%',
            '>= 15%',
          ),
        ]),
        makeNode('coarse_sand_fines_gt12_pi_clay', 'PI >= 7 and PI >= A-line', [
          makeCoarseGroupNode(
            'coarse_sand_fines_gt12_pi_clay',
            'SC',
            'Clayey sand',
            'Clayey sand with gravel',
            'Gravel < 15%?',
            '< 15%',
            '>= 15%',
          ),
        ]),
        makeNode('coarse_sand_fines_gt12_pi_mixed', '4 <= PI <= 7 or PI == A-line', [
          makeCoarseGroupNode(
            'coarse_sand_fines_gt12_pi_mixed',
            'SC-SM',
            'Silty, clayey sand',
            'Silty, clayey sand with gravel',
            'Gravel < 15%?',
            '< 15%',
            '>= 15%',
          ),
        ]),
      ]),
    ]),
  ]);

const USCS_TREE = makeNode('root', 'Start', [
  { ...buildFineBranch(), edge: 'P200 >= 50%' },
  { ...buildCoarseBranch(), edge: 'P200 < 50%' },
]);

const findPathByGroup = (node, groupSymbol, groupName, path = []) => {
  const nextPath = [...path, node];
  const targetLabel = `Group name: ${groupName}`;
  if (node.label === targetLabel) {
    if (!groupSymbol) return nextPath;
    const symbolLabel = `Group symbol: ${groupSymbol}`;
    const hasSymbol = nextPath.some((entry) => entry.label === symbolLabel);
    return hasSymbol ? nextPath : null;
  }
  if (!node.children || node.children.length === 0) return null;
  for (const child of node.children) {
    const result = findPathByGroup(child, groupSymbol, groupName, nextPath);
    if (result) return result;
  }
  return null;
};

const formatLabel = (label) => (typeof label === 'string' ? label.replaceAll('>=', '≥').replaceAll('<=', '≤') : label);

const buildActiveEdgeIds = (path) => {
  if (!path || path.length < 2) return new Set();
  const edgeIds = new Set();
  for (let i = 1; i < path.length; i += 1) {
    edgeIds.add(path[i].id);
  }
  return edgeIds;
};

const TreeNode = ({ node, activeIds, activeEdgeIds, collapsedIds, animateNodes, setAnimateNodes }) => {
  const isActive = activeIds.has(node.id);
  const isCollapsed = collapsedIds.has(node.id);
  const shouldAnimate = animateNodes.has(node.id);
  return (
    <div className={`uscsTreeNode ${isActive ? 'is-active' : ''}`}>
      <div className="uscsTreeNodeLabel">
        {node.children && node.children.length > 0 ? (
          <button
            type="button"
            className="uscsTreeCollapseBtn"
            onClick={(e) => {
              e.stopPropagation();
              setAnimateNodes(new Set([node.id]));
              collapsedIds.toggle(node.id);
            }}
            aria-label={isCollapsed ? 'Expand branch' : 'Collapse branch'}
            title={isCollapsed ? 'Expand branch' : 'Collapse branch'}
          >
            {isCollapsed ? '+' : '-'}
          </button>
        ) : (
          <span className="uscsTreeCollapseSpacer" />
        )}
        <span
          className={`uscsTreeNodeText ${
            isActive && typeof node.label === 'string' && node.label.startsWith('Group name:') ? 'uscsTreeResultChip' : ''
          }`}
        >
          {formatLabel(node.label)}
        </span>
      </div>
      {node.children && node.children.length > 0 && (
        <Collapse in={!isCollapsed} timeout={shouldAnimate ? 200 : 0} appear={false}>
          <div className={`uscsTreeChildren ${isActive ? 'is-active' : ''}`}>
            {node.children.map((child) => {
              const edgeActive = activeEdgeIds.has(child.id);
              return (
                <div className={`uscsTreeEdge ${edgeActive ? 'is-active' : ''}`} key={child.id}>
                  {child.edge && <div className="uscsTreeEdgeLabel">{formatLabel(child.edge)}</div>}
                  <TreeNode
                    node={child}
                    activeIds={activeIds}
                    activeEdgeIds={activeEdgeIds}
                    collapsedIds={collapsedIds}
                    animateNodes={animateNodes}
                    setAnimateNodes={setAnimateNodes}
                  />
                </div>
              );
            })}
          </div>
        </Collapse>
      )}
    </div>
  );
};

const markFineRetainedPath = (active, prefix, retained200, sand, gravel) => {
  active.add(`${prefix}_retained`);
  if (retained200 < 30) {
    active.add(`${prefix}_ret_lt30`);
    if (retained200 < 15) {
      active.add(`${prefix}_ret_lt15`);
    } else {
      active.add(`${prefix}_ret_15_30`);
      if (sand >= gravel) {
        active.add(`${prefix}_ret_15_30_sand_ge`);
      } else {
        active.add(`${prefix}_ret_15_30_sand_lt`);
      }
    }
  } else {
    active.add(`${prefix}_ret_ge30`);
    if (sand >= gravel) {
      active.add(`${prefix}_ret_ge30_sand_ge`);
      if (gravel < 15) {
        active.add(`${prefix}_ret_ge30_sand_ge_gravel_lt`);
      } else {
        active.add(`${prefix}_ret_ge30_sand_ge_gravel_ge`);
      }
    } else {
      active.add(`${prefix}_ret_ge30_sand_lt`);
      if (sand < 15) {
        active.add(`${prefix}_ret_ge30_sand_lt_sand_lt`);
      } else {
        active.add(`${prefix}_ret_ge30_sand_lt_sand_ge`);
      }
    }
  }
};

const buildActivePath = (result) => {
  const active = new Set();
  if (!result || !result.inputs) return active;

  const p200 = toNumber(result.inputs.percentPassing200);
  const p4 = toNumber(result.inputs.percentPassing4);
  const ll = toNumber(result.inputs.liquidLimit);
  const pl = toNumber(result.inputs.plasticLimit);
  const d10 = toNumber(result.inputs.D10);
  const d30 = toNumber(result.inputs.D30);
  const d60 = toNumber(result.inputs.D60);

  if (!Number.isFinite(p200) || !Number.isFinite(p4)) return active;

  const retained200 = 100 - p200;
  const sand = p4 - p200;
  const gravel = 100 - p4;
  const isFine = p200 >= 50;

  active.add('root');

  if (isFine) {
    active.add('fine_branch');
    if (!Number.isFinite(ll) || !Number.isFinite(pl)) return active;
    const pi = ll - pl;
    const aLine = ALine(ll);
    const isOrganic = !!result.inputs.isOrganic;

    if (ll < 50) {
      active.add('fine_ll_lt50');
      if (!isOrganic) {
        active.add('fine_ll_lt50_inorg');
        active.add('fine_ll_lt50_inorg_pi');
        if (pi > 7 && pi >= aLine) {
          active.add('fine_inorg_lt50_cl_symbol');
          markFineRetainedPath(active, 'fine_inorg_lt50_cl', retained200, sand, gravel);
        } else if (pi >= 4 && pi <= 7 && pi >= aLine) {
          active.add('fine_inorg_lt50_clml_symbol');
          markFineRetainedPath(active, 'fine_inorg_lt50_clml', retained200, sand, gravel);
        } else if (pi < 4 || pi < aLine) {
          active.add('fine_inorg_lt50_ml_symbol');
          markFineRetainedPath(active, 'fine_inorg_lt50_ml', retained200, sand, gravel);
        }
      } else {
        active.add('fine_ll_lt50_org');
        active.add('fine_ll_lt50_org_pi');
        if (pi >= 4 && pi >= aLine) {
          active.add('fine_org_lt50_ol_clay_symbol');
          markFineRetainedPath(active, 'fine_org_lt50_ol_clay', retained200, sand, gravel);
        } else if (pi < 4 || pi < aLine) {
          active.add('fine_org_lt50_ol_silt_symbol');
          markFineRetainedPath(active, 'fine_org_lt50_ol_silt', retained200, sand, gravel);
        }
      }
    } else {
      active.add('fine_ll_gte50');
      if (!isOrganic) {
        active.add('fine_ll_gte50_inorg');
        active.add('fine_ll_gte50_inorg_pi');
        if (pi >= aLine) {
          active.add('fine_inorg_gte50_ch_symbol');
          markFineRetainedPath(active, 'fine_inorg_gte50_ch', retained200, sand, gravel);
        } else if (pi < aLine) {
          active.add('fine_inorg_gte50_mh_symbol');
          markFineRetainedPath(active, 'fine_inorg_gte50_mh', retained200, sand, gravel);
        }
      } else {
        active.add('fine_ll_gte50_org');
        active.add('fine_ll_gte50_org_pi');
        if (pi >= aLine) {
          active.add('fine_org_gte50_oh_clay_symbol');
          markFineRetainedPath(active, 'fine_org_gte50_oh_clay', retained200, sand, gravel);
        } else if (pi < aLine) {
          active.add('fine_org_gte50_oh_silt_symbol');
          markFineRetainedPath(active, 'fine_org_gte50_oh_silt', retained200, sand, gravel);
        }
      }
    }
  } else {
    active.add('coarse_branch');
    const fines = p200;
    const isGravelDominant = gravel >= sand;
    const pi = ll - pl;
    const aLine = ALine(ll);
    const cc = (d30 * d30) / (d10 * d60);
    const cu = d60 / d10;

    if (isGravelDominant) {
      active.add('coarse_gravel_dominant');
      if (fines < 5) {
        active.add('coarse_gravel_fines_lt5');
        if (cu >= 4 && cc >= 1 && cc <= 3) {
          active.add('coarse_gravel_fines_lt5_cucc_good');
          active.add('coarse_gravel_fines_lt5_cucc_good_symbol');
          active.add('coarse_gravel_fines_lt5_cucc_good_content');
          if (sand < 15) {
            active.add('coarse_gravel_fines_lt5_cucc_good_content_low');
          } else {
            active.add('coarse_gravel_fines_lt5_cucc_good_content_high');
          }
        } else {
          active.add('coarse_gravel_fines_lt5_cucc_poor');
          active.add('coarse_gravel_fines_lt5_cucc_poor_symbol');
          active.add('coarse_gravel_fines_lt5_cucc_poor_content');
          if (sand < 15) {
            active.add('coarse_gravel_fines_lt5_cucc_poor_content_low');
          } else {
            active.add('coarse_gravel_fines_lt5_cucc_poor_content_high');
          }
        }
      } else if (fines >= 5 && fines <= 12) {
        active.add('coarse_gravel_fines_5_12');
        if (cu >= 4 && cc >= 1 && cc <= 3) {
          active.add('coarse_gravel_fines_5_12_cucc_good');
          if (pi < 4 || pi < aLine) {
            active.add('coarse_gravel_fines_5_12_cucc_good_pi_silt');
            active.add('coarse_gravel_fines_5_12_cucc_good_pi_silt_symbol');
            active.add('coarse_gravel_fines_5_12_cucc_good_pi_silt_content');
            if (sand < 15) {
              active.add('coarse_gravel_fines_5_12_cucc_good_pi_silt_content_low');
            } else {
              active.add('coarse_gravel_fines_5_12_cucc_good_pi_silt_content_high');
            }
          } else {
            active.add('coarse_gravel_fines_5_12_cucc_good_pi_clay');
            active.add('coarse_gravel_fines_5_12_cucc_good_pi_clay_symbol');
            active.add('coarse_gravel_fines_5_12_cucc_good_pi_clay_content');
            if (sand < 15) {
              active.add('coarse_gravel_fines_5_12_cucc_good_pi_clay_content_low');
            } else {
              active.add('coarse_gravel_fines_5_12_cucc_good_pi_clay_content_high');
            }
          }
        } else {
          active.add('coarse_gravel_fines_5_12_cucc_poor');
          if (pi < 4 || pi < aLine) {
            active.add('coarse_gravel_fines_5_12_cucc_poor_pi_silt');
            active.add('coarse_gravel_fines_5_12_cucc_poor_pi_silt_symbol');
            active.add('coarse_gravel_fines_5_12_cucc_poor_pi_silt_content');
            if (sand < 15) {
              active.add('coarse_gravel_fines_5_12_cucc_poor_pi_silt_content_low');
            } else {
              active.add('coarse_gravel_fines_5_12_cucc_poor_pi_silt_content_high');
            }
          } else {
            active.add('coarse_gravel_fines_5_12_cucc_poor_pi_clay');
            active.add('coarse_gravel_fines_5_12_cucc_poor_pi_clay_symbol');
            active.add('coarse_gravel_fines_5_12_cucc_poor_pi_clay_content');
            if (sand < 15) {
              active.add('coarse_gravel_fines_5_12_cucc_poor_pi_clay_content_low');
            } else {
              active.add('coarse_gravel_fines_5_12_cucc_poor_pi_clay_content_high');
            }
          }
        }
      } else if (fines > 12) {
        active.add('coarse_gravel_fines_gt12');
        if (pi < 4 || pi < aLine) {
          active.add('coarse_gravel_fines_gt12_pi_silt');
          active.add('coarse_gravel_fines_gt12_pi_silt_symbol');
          active.add('coarse_gravel_fines_gt12_pi_silt_content');
          if (sand < 15) {
            active.add('coarse_gravel_fines_gt12_pi_silt_content_low');
          } else {
            active.add('coarse_gravel_fines_gt12_pi_silt_content_high');
          }
        } else if (pi >= 7 && pi >= aLine) {
          active.add('coarse_gravel_fines_gt12_pi_clay');
          active.add('coarse_gravel_fines_gt12_pi_clay_symbol');
          active.add('coarse_gravel_fines_gt12_pi_clay_content');
          if (sand < 15) {
            active.add('coarse_gravel_fines_gt12_pi_clay_content_low');
          } else {
            active.add('coarse_gravel_fines_gt12_pi_clay_content_high');
          }
        } else {
          active.add('coarse_gravel_fines_gt12_pi_mixed');
          active.add('coarse_gravel_fines_gt12_pi_mixed_symbol');
          active.add('coarse_gravel_fines_gt12_pi_mixed_content');
          if (sand < 15) {
            active.add('coarse_gravel_fines_gt12_pi_mixed_content_low');
          } else {
            active.add('coarse_gravel_fines_gt12_pi_mixed_content_high');
          }
        }
      }
    } else {
      active.add('coarse_sand_dominant');
      if (fines < 5) {
        active.add('coarse_sand_fines_lt5');
        if (cu >= 6 && cc >= 1 && cc <= 3) {
          active.add('coarse_sand_fines_lt5_cucc_good');
          active.add('coarse_sand_fines_lt5_cucc_good_symbol');
          active.add('coarse_sand_fines_lt5_cucc_good_content');
          if (gravel < 15) {
            active.add('coarse_sand_fines_lt5_cucc_good_content_low');
          } else {
            active.add('coarse_sand_fines_lt5_cucc_good_content_high');
          }
        } else {
          active.add('coarse_sand_fines_lt5_cucc_poor');
          active.add('coarse_sand_fines_lt5_cucc_poor_symbol');
          active.add('coarse_sand_fines_lt5_cucc_poor_content');
          if (gravel < 15) {
            active.add('coarse_sand_fines_lt5_cucc_poor_content_low');
          } else {
            active.add('coarse_sand_fines_lt5_cucc_poor_content_high');
          }
        }
      } else if (fines >= 5 && fines <= 12) {
        active.add('coarse_sand_fines_5_12');
        if (cu >= 6 && cc >= 1 && cc <= 3) {
          active.add('coarse_sand_fines_5_12_cucc_good');
          if (pi < 4 || pi < aLine) {
            active.add('coarse_sand_fines_5_12_cucc_good_pi_silt');
            active.add('coarse_sand_fines_5_12_cucc_good_pi_silt_symbol');
            active.add('coarse_sand_fines_5_12_cucc_good_pi_silt_content');
            if (gravel < 15) {
              active.add('coarse_sand_fines_5_12_cucc_good_pi_silt_content_low');
            } else {
              active.add('coarse_sand_fines_5_12_cucc_good_pi_silt_content_high');
            }
          } else {
            active.add('coarse_sand_fines_5_12_cucc_good_pi_clay');
            active.add('coarse_sand_fines_5_12_cucc_good_pi_clay_symbol');
            active.add('coarse_sand_fines_5_12_cucc_good_pi_clay_content');
            if (gravel < 15) {
              active.add('coarse_sand_fines_5_12_cucc_good_pi_clay_content_low');
            } else {
              active.add('coarse_sand_fines_5_12_cucc_good_pi_clay_content_high');
            }
          }
        } else {
          active.add('coarse_sand_fines_5_12_cucc_poor');
          if (pi < 4 || pi < aLine) {
            active.add('coarse_sand_fines_5_12_cucc_poor_pi_silt');
            active.add('coarse_sand_fines_5_12_cucc_poor_pi_silt_symbol');
            active.add('coarse_sand_fines_5_12_cucc_poor_pi_silt_content');
            if (gravel < 15) {
              active.add('coarse_sand_fines_5_12_cucc_poor_pi_silt_content_low');
            } else {
              active.add('coarse_sand_fines_5_12_cucc_poor_pi_silt_content_high');
            }
          } else {
            active.add('coarse_sand_fines_5_12_cucc_poor_pi_clay');
            active.add('coarse_sand_fines_5_12_cucc_poor_pi_clay_symbol');
            active.add('coarse_sand_fines_5_12_cucc_poor_pi_clay_content');
            if (gravel < 15) {
              active.add('coarse_sand_fines_5_12_cucc_poor_pi_clay_content_low');
            } else {
              active.add('coarse_sand_fines_5_12_cucc_poor_pi_clay_content_high');
            }
          }
        }
      } else if (fines > 12) {
        active.add('coarse_sand_fines_gt12');
        if (pi < 4 || pi < aLine) {
          active.add('coarse_sand_fines_gt12_pi_silt');
          active.add('coarse_sand_fines_gt12_pi_silt_symbol');
          active.add('coarse_sand_fines_gt12_pi_silt_content');
          if (gravel < 15) {
            active.add('coarse_sand_fines_gt12_pi_silt_content_low');
          } else {
            active.add('coarse_sand_fines_gt12_pi_silt_content_high');
          }
        } else if (pi >= 7 && pi >= aLine) {
          active.add('coarse_sand_fines_gt12_pi_clay');
          active.add('coarse_sand_fines_gt12_pi_clay_symbol');
          active.add('coarse_sand_fines_gt12_pi_clay_content');
          if (gravel < 15) {
            active.add('coarse_sand_fines_gt12_pi_clay_content_low');
          } else {
            active.add('coarse_sand_fines_gt12_pi_clay_content_high');
          }
        } else {
          active.add('coarse_sand_fines_gt12_pi_mixed');
          active.add('coarse_sand_fines_gt12_pi_mixed_symbol');
          active.add('coarse_sand_fines_gt12_pi_mixed_content');
          if (gravel < 15) {
            active.add('coarse_sand_fines_gt12_pi_mixed_content_low');
          } else {
            active.add('coarse_sand_fines_gt12_pi_mixed_content_high');
          }
        }
      }
    }
  }

  return active;
};

const buildActiveOnlyCollapsed = (activeIds) => {
  const next = new Set();
  const walk = (node) => {
    if (node.children && node.children.length > 0) {
      if (!activeIds.has(node.id)) {
        next.add(node.id);
      } else {
        node.children.forEach(walk);
      }
    }
  };
  walk(USCS_TREE);
  return next;
};

const setEquals = (left, right) => {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
};

const buildNotesList = (classification, preliminaryClassificationNotes, fineGrainedNotes, coarseGrainedNotes) => {
  const notes = [];
  if (preliminaryClassificationNotes) notes.push(preliminaryClassificationNotes);
  if (classification === 'fine-grained' && fineGrainedNotes) {
    const order = [
      'liquidLimit',
      'organic',
      'PIAndALine',
      'no200RetainedOverOrUnder30',
      'no200RetainedBetween15and30',
      'sandGravelComparison',
      'sandOrGravelContent',
    ];
    order.forEach((key) => {
      const value = fineGrainedNotes[key];
      if (value) notes.push(value);
    });
  }
  if (classification === 'coarse-grained' && coarseGrainedNotes) {
    const order = ['sandOrGravel', 'finesPercent', 'cUandCc', 'finesContent', 'sandGravelComparison'];
    order.forEach((key) => {
      const value = coarseGrainedNotes[key];
      if (value) notes.push(value);
    });
  }
  return notes;
};

const ConsolidationResult = ({ result }) => {
  const {
    preliminaryClassification,
    preliminaryClassificationNotes,
    fineGrainedNotes,
    coarseGrainedNotes,
    groupSymbol,
    groupName,
    canUserSeeCalculationSteps,
  } = result.outputs;
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const preferredPath = useMemo(() => {
    if (!result?.outputs) return null;
    return findPathByGroup(USCS_TREE, result.outputs.groupSymbol, result.outputs.groupName);
  }, [result?.outputs?.groupSymbol, result?.outputs?.groupName]);
  const activeIds = useMemo(
    () => (preferredPath ? new Set(preferredPath.map((entry) => entry.id)) : buildActivePath(result)),
    [preferredPath, result],
  );
  const activeEdgeIds = useMemo(
    () => (preferredPath ? buildActiveEdgeIds(preferredPath) : activeIds),
    [preferredPath, activeIds],
  );
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [viewMode, setViewMode] = useState('active');
  const [collapsedSet, setCollapsedSet] = useState(new Set());
  const [animateNodes, setAnimateNodes] = useState(new Set());
  const [showNotesDetails, setShowNotesDetails] = useState(false);
  const collapsedIds = useMemo(() => {
    const api = new Set(collapsedSet);
    api.toggle = (id) => {
      const next = new Set(api);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      setCollapsedSet(next);
    };
    return api;
  }, [collapsedSet]);
  useEffect(() => {
    if (showActiveOnly) {
      setCollapsedSet((prev) => {
        const next = buildActiveOnlyCollapsed(activeIds);
        return setEquals(prev, next) ? prev : next;
      });
    }
  }, [showActiveOnly, activeIds]);

  const notesList = useMemo(
    () => buildNotesList(preliminaryClassification, preliminaryClassificationNotes, fineGrainedNotes, coarseGrainedNotes),
    [preliminaryClassification, preliminaryClassificationNotes, fineGrainedNotes, coarseGrainedNotes],
  );
  const firstNote = preliminaryClassificationNotes || notesList[0];
  const remainingNotes = notesList.slice(1);
  const nextNoteTeaser =
    remainingNotes.length > 0 ? `${remainingNotes[0].slice(0, 120)}${remainingNotes[0].length > 120 ? '...' : ''}` : '';

  const expandAll = () => {
    setAnimateNodes(new Set());
    setShowActiveOnly(false);
    setCollapsedSet(new Set());
  };
  const collapseAll = () => {
    setAnimateNodes(new Set());
    setShowActiveOnly(false);
    const all = new Set();
    const walk = (node) => {
      if (node.children && node.children.length > 0) {
        all.add(node.id);
        node.children.forEach(walk);
      }
    };
    walk(USCS_TREE);
    setCollapsedSet(all);
  };
  const handleViewModeChange = (event, nextMode) => {
    if (!nextMode) return;
    setViewMode(nextMode);
    if (nextMode === 'active') {
      setAnimateNodes(new Set());
      setShowActiveOnly(true);
      return;
    }
    setShowActiveOnly(false);
    if (nextMode === 'expand') {
      expandAll();
    }
    if (nextMode === 'collapse') {
      collapseAll();
    }
  };

  return (
    <section className="output uscsClassifierResult">
      <h2>Output</h2>
      <div className="uscsTreeSection">
        <Alert severity="info" variant="outlined" className="uscsTreeContainer">
          <div className="uscsTreeHeader">
            <Typography variant="subtitle1" color="text.secondary" fontWeight={600} gutterBottom>
              USCS Soil Classification Decision Tree
            </Typography>
            <div className="uscsTreeControls">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={viewMode}
                onChange={handleViewModeChange}
                color="primary"
                aria-label="Tree view mode"
              >
                <ToggleButton value="expand">Expand all</ToggleButton>
                <ToggleButton value="collapse">Collapse all</ToggleButton>
                <ToggleButton value="active">Show active only</ToggleButton>
              </ToggleButtonGroup>
            </div>
          </div>
          <div
            className={`uscsTreeColumns ${
              preliminaryClassification !== 'coarse-grained' && preliminaryClassification !== 'fine-grained'
                ? 'has-two-columns'
                : ''
            }`}
          >
            {preliminaryClassification !== 'coarse-grained' && (
              <div className="uscsTreeColumn">
                <TreeNode
                  node={USCS_TREE.children[0]}
                  activeIds={activeIds}
                  activeEdgeIds={activeEdgeIds}
                  collapsedIds={collapsedIds}
                  animateNodes={animateNodes}
                  setAnimateNodes={setAnimateNodes}
                />
              </div>
            )}
            {preliminaryClassification !== 'fine-grained' && (
              <div className="uscsTreeColumn">
                <TreeNode
                  node={USCS_TREE.children[1]}
                  activeIds={activeIds}
                  activeEdgeIds={activeEdgeIds}
                  collapsedIds={collapsedIds}
                  animateNodes={animateNodes}
                  setAnimateNodes={setAnimateNodes}
                />
              </div>
            )}
          </div>
        </Alert>
      </div>
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} gutterBottom>
            USCS Decision Notes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            The notes below describe each decision step taken to reach your final USCS group symbol and name. Expand to see the
            full sequence of decisions. These notes summarize the branch choices made from your inputs for quick review.
          </Typography>
          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(46,125,50,0.04)',
              alignItems: 'flex-start',
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24,
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' },
            }}
            action={
              remainingNotes.length > 0 ? (
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowNotesDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showNotesDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to see all decision notes.{' '}
                        <Link href="/pricing" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              ) : null
            }
          >
            <Typography variant="body2" color="text.secondary">
              {firstNote || '—'}
            </Typography>
            {!showNotesDetails && remainingNotes.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                {nextNoteTeaser}
              </Typography>
            )}
            {canUserSeeCalculationSteps && remainingNotes.length > 0 && (
              <Collapse in={showNotesDetails} unmountOnExit>
                {preliminaryClassification === 'fine-grained' && (
                  <div className="uscsNotes">
                    <SouthOutlinedIcon />
                    <div className="uscsClassifierBranchNode">{preliminaryClassification}</div>
                    <div className="uscsClassifierNotes">{fineGrainedNotes.liquidLimit}</div>
                    <div className="uscsClassifierNotes">{fineGrainedNotes.organic}</div>
                    <SouthOutlinedIcon />
                    <div className="uscsClassifierBranchNode">{result.inputs.isOrganic ? 'organic' : 'inorganic'}</div>
                    <div className="uscsClassifierNotes">{fineGrainedNotes.PIAndALine}</div>
                    <SouthOutlinedIcon />
                    <div className="uscsClassifierBranchNode orangeBackground"> {groupSymbol}</div>
                    <div className="uscsClassifierNotes">{fineGrainedNotes.no200RetainedOverOrUnder30}</div>
                    <div className="uscsClassifierNotes">{fineGrainedNotes.no200RetainedBetween15and30}</div>
                    {fineGrainedNotes.sandGravelComparison && (
                      <div className="uscsClassifierNotes">{fineGrainedNotes.sandGravelComparison}</div>
                    )}
                    {fineGrainedNotes.sandOrGravelContent && (
                      <div className="uscsClassifierNotes">{fineGrainedNotes.sandOrGravelContent}</div>
                    )}
                    <SouthOutlinedIcon />
                    <div className="uscsClassifierBranchNode orangeBackground">{groupName}</div>
                  </div>
                )}
                {preliminaryClassification === 'coarse-grained' && (
                  <div className="uscsNotes">
                    <SouthOutlinedIcon />
                    <div className="uscsClassifierBranchNode">{preliminaryClassification}</div>
                    <div className="uscsClassifierNotes">{coarseGrainedNotes.sandOrGravel}</div>
                    <div className="uscsClassifierNotes">{coarseGrainedNotes.finesPercent}</div>
                    <div className="uscsClassifierNotes">{coarseGrainedNotes.cUandCc}</div>
                    <div className="uscsClassifierNotes">{coarseGrainedNotes.finesContent}</div>
                    <SouthOutlinedIcon />
                    <div className="uscsClassifierBranchNode orangeBackground"> {groupSymbol}</div>
                    <div className="uscsClassifierNotes">{coarseGrainedNotes.sandGravelComparison}</div>
                    <SouthOutlinedIcon />
                    <div className="uscsClassifierBranchNode orangeBackground">{groupName}</div>
                  </div>
                )}
              </Collapse>
            )}
          </Alert>
        </Alert>
      </Box>
    </section>
  );
};

export default ConsolidationResult;
